import { useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Modal, Select, Divider, Stack, Group, Checkbox } from '@mantine/core'
import { useForm } from '@mantine/form'
import type { GetDriverIQUdfFields } from 'types/graphql'

import { useQuery, useMutation } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import StyledAlert from 'src/components/Feedback/StyledAlert/StyledAlert'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_DRIVER_IQ_UDF_FIELDS } from 'src/graphql/driverIQUdfFields.graphql'
import { CREATE_PACKAGE_ORDER } from 'src/graphql/packageOrders.graphql'
import { REQUEST_PACKAGES } from 'src/graphql/packages.graphql'
import { PackageType, PackageTypeLabels } from 'src/graphql/types/package'
import { StepType } from 'src/graphql/types/steps'
import { formatStepType } from 'src/lib/formatters'

type UdfField = GetDriverIQUdfFields['driverIQUdfFields'][number]

interface RequestOrderFormValues {
  packageId: string
  fcraLegalRequirementsConsent: boolean
  currentLicenseOnly: boolean
  /** DriverIQ UDF slot (UDF1|UDF2|UDF3) -> selected option id */
  driverIQUdfSelections: Record<string, string>
}

interface RequestOrderModalProps {
  isModalOpen: boolean
  onModalClose: () => void
  refetchQueries?: any[]
  applicantId: string
}

interface OptionType {
  value: string
  label: string
  stepTypes: StepType[]
  packageType: PackageType
  disabled?: boolean
}

/**
 * The API refuses HireRight employment-verification orders when no employer may
 * be contacted (ENG-4846).
 */
const EMPLOYER_CONSENT_BLOCK_PATTERN =
  /declined contact for every employer|has no employers listed|no employer is ready to verify yet/i

export const isEmployerConsentBlockedError = (message?: string | null) =>
  !!message && EMPLOYER_CONSENT_BLOCK_PATTERN.test(message)

const RequestOrderModal = ({
  isModalOpen,
  onModalClose,
  refetchQueries,
  applicantId,
}: RequestOrderModalProps) => {
  const [errorMessage, setErrorMessage] = useState(null)
  const [consentBlockMessage, setConsentBlockMessage] = useState<string | null>(
    null
  )
  const [successMessage, setSuccessMessage] = useState(null)
  const [warningVisible, setWarningVisible] = useState(false)
  const [overrideDuplicate, setOverrideDuplicate] = useState(false)
  const [isDuplicated, setIsDuplicated] = useState(false)
  const apolloClient = useApolloClient()

  const form = useForm<RequestOrderFormValues>({
    initialValues: {
      packageId: '',
      fcraLegalRequirementsConsent: false,
      currentLicenseOnly: false,
      driverIQUdfSelections: {},
    },
  })

  const { data } = useQuery(REQUEST_PACKAGES)
  const { data: udfData } = useQuery<GetDriverIQUdfFields>(
    GET_DRIVER_IQ_UDF_FIELDS
  )
  const udfFields: UdfField[] =
    udfData?.driverIQUdfFields
      ?.slice()
      .sort((a, b) => a.sortOrder - b.sortOrder) || []
  const packagesList =
    data?.packages
      ?.slice()
      .sort((a, b) => {
        if (a.packageType < b.packageType) return -1
        if (a.packageType > b.packageType) return 1
        return a.name.localeCompare(b.name)
      })
      .map((p) => ({
        value: p.packageId,
        label: `${PackageTypeLabels[p.packageType]} - ${p.name}`,
        stepTypes: p.stepType,
        packageType: p.packageType,
      })) || []

  const selectedPackage = packagesList.find(
    (p) => p.value === form.values.packageId
  )
  const isSelectedHireRight =
    selectedPackage?.packageType === PackageType.HIRE_RIGHT
  const isDriverIQ = selectedPackage?.packageType === PackageType.DRIVER_IQ
  // The toggle only changes what MVR-bearing packages receive, so it is only
  // offered there. Guarding the submitted value too means a box checked for
  // one package can't leak into an order for a package that never showed it.
  const showCurrentLicenseOnly = !!selectedPackage?.stepTypes?.includes(
    StepType.MVR
  )
  const currentLicenseOnly =
    showCurrentLicenseOnly && form.values.currentLicenseOnly

  // UDF dropdowns are only meaningful for DriverIQ orders. Like the MVR
  // toggle, the submitted value is guarded too so selections made while a
  // DriverIQ package was highlighted never ride along on another provider.
  const showUdfFields = isDriverIQ && udfFields.length > 0
  const missingRequiredUdf =
    isDriverIQ &&
    udfFields.some(
      (f) => f.isRequired && !form.values.driverIQUdfSelections[f.slot]
    )
  const udfSelectionsInput = isDriverIQ
    ? Object.entries(form.values.driverIQUdfSelections)
        .filter(([, optionId]) => !!optionId)
        .map(([slot, optionId]) => ({
          slot,
          driverIQUdfFieldOptionId: optionId,
        }))
    : []

  const closeConsentModal = () => setConsentBlockMessage(null)

  const resetAlerts = () => {
    setWarningVisible(false)
    setErrorMessage(null)
    setConsentBlockMessage(null)
    setSuccessMessage(null)
  }

  const handleSubmit = (values) => {
    resetAlerts()

    createPackageOrder({
      variables: {
        input: {
          packageId: values.packageId,
          applicantId,
          // Only while the duplicate warning is on screen: a later non-duplicate
          // error hides the checkbox but leaves its checked state behind.
          overrideDuplicateDetection: isDuplicated && overrideDuplicate,
          fcraLegalRequirementsConsent: values.fcraLegalRequirementsConsent,
          forceGenerateWebLink: false,
          currentLicenseOnly,
          driverIQUdfSelections: udfSelectionsInput,
        },
      },
    })
  }

  const handleForceFallback = (values) => {
    resetAlerts()
    createPackageOrder({
      variables: {
        input: {
          packageId: values.packageId,
          applicantId,
          overrideDuplicateDetection: false,
          fcraLegalRequirementsConsent: values.fcraLegalRequirementsConsent,
          forceGenerateWebLink: true,
          currentLicenseOnly,
          driverIQUdfSelections: udfSelectionsInput,
        },
      },
      onCompleted: (res) => {
        const { generatedWebLink, errorMessage: retryError } =
          res?.createPackageOrder || {}
        if (generatedWebLink) {
          window.open(generatedWebLink, '_blank')
          toast('Order created successfully via alternative link.', 'success')
          closeModal()
        } else {
          setErrorMessage(retryError || 'Failed to generate web link')
        }
      },
      onError: (err) => {
        setErrorMessage(err.message)
      },
    })
  }

  const closeModal = () => {
    form.reset()
    resetAlerts()
    setOverrideDuplicate(false)
    setIsDuplicated(false)
    apolloClient.refetchQueries({ include: refetchQueries })
    onModalClose()
  }

  const [createPackageOrder, { loading: createPackageOrderLoading }] =
    useMutation(CREATE_PACKAGE_ORDER, {
      onCompleted: (res) => {
        const {
          packageOrderId,
          providerOrderId,
          generatedWebLink,
          errorMessage,
          isDuplicated,
        } = res?.createPackageOrder || {}

        // Check if error message contains "duplicate" and set flag locally
        const isDuplicateDetected =
          isDuplicated ||
          (errorMessage && errorMessage.toLowerCase().includes('duplicate'))
        setIsDuplicated(isDuplicateDetected)

        if (isDuplicateDetected) {
          setErrorMessage(
            <span
              data-testid="duplicate-message"
              style={{ whiteSpace: 'pre-line' }}
            >
              {errorMessage}
            </span>
          )
          return
        }

        // Get the selected package type to determine the provider and package specifics
        const selectedPackage = packagesList.find(
          (p) => p.value === form.values.packageId
        )
        const providerName =
          PackageTypeLabels[selectedPackage?.packageType] || 'provider'
        const isHireRight =
          selectedPackage?.packageType === PackageType.HIRE_RIGHT
        const isAsurint = selectedPackage?.packageType === PackageType.ASURINT
        const normalizedGeneratedLink = generatedWebLink?.trim().toLowerCase()

        // HireRight errors without link (not duplicates): show CTA only for specific error types
        if (errorMessage && isHireRight && !generatedWebLink) {
          const isInvalidMissingDataError =
            errorMessage.toLowerCase().includes('invalid fields') ||
            errorMessage.toLowerCase().includes('invalid/missing data') ||
            errorMessage
              .toLowerCase()
              .includes('unable to determine ordering account') ||
            errorMessage.toLowerCase().includes('missing required field')

          // Only show correction message for specific error types
          if (isInvalidMissingDataError) {
            setErrorMessage(
              <span data-testid="faceless-error-message">
                Please correct any missing or invalid data in the fields listed
                below and resubmit the order. Alternatively,
                <button
                  className="cursor-pointer text-blue-600 underline hover:text-blue-800"
                  onClick={() => handleForceFallback(form.values)}
                  type="button"
                >
                  click here
                </button>{' '}
                to complete the order submission within {providerName}.&nbsp;
                <p style={{ whiteSpace: 'pre-line' }}>{errorMessage}</p>
              </span>
            )
            return
          }

          // For other error types, just show the error message
          setErrorMessage(
            <span
              data-testid="pending-requestor-message"
              style={{ whiteSpace: 'pre-line' }}
            >
              {errorMessage}
            </span>
          )
          return
        }

        // Special case: Unable to determine ordering account with link to complete
        if (
          generatedWebLink &&
          errorMessage === 'Unable to determine ordering account'
        ) {
          setErrorMessage(
            <span data-testid="unable-to-determine-ordering-account-message">
              Unable to determine ordering account. Please, click{' '}
              <a
                className="text-blue-600 underline hover:text-blue-800"
                target="_blank"
                href={generatedWebLink}
                rel="noreferrer"
              >
                here
              </a>{' '}
              to complete the order submission on the {providerName} side.&nbsp;
            </span>
          )
          return
        }

        // Error with generated link: show message including the link
        if (generatedWebLink && errorMessage) {
          setErrorMessage(
            <span data-testid="pending-requestor-message">
              Please correct any missing or invalid data in the fields listed
              below and resubmit the order. Alternatively, click{' '}
              <a
                className="text-blue-600 underline hover:text-blue-800"
                target="_blank"
                href={generatedWebLink}
                rel="noreferrer"
              >
                here
              </a>{' '}
              to complete the order submission within {providerName}.&nbsp;
              <p style={{ whiteSpace: 'pre-line' }}>{errorMessage}</p>
            </span>
          )
          return
        }

        if (
          isAsurint &&
          generatedWebLink &&
          normalizedGeneratedLink === 'email sent to candidate'
        ) {
          toast(
            `Order requested successfuly. Number: ${
              providerOrderId || packageOrderId
            }.`,
            'success'
          )
          closeModal()
          return
        }

        // Successful orders with generated link
        if (generatedWebLink && providerName === 'Asurint') {
          setSuccessMessage(
            <span data-testid="pending-requestor-message">
              {providerName} needs a few more details to finish this order.
              Click{' '}
              <a
                className="text-blue-600 underline hover:text-blue-800"
                target="_blank"
                href={generatedWebLink}
                rel="noreferrer"
              >
                here
              </a>{' '}
              to complete it in {providerName}. &nbsp;
              {errorMessage && (
                <p style={{ whiteSpace: 'pre-line' }}>{errorMessage}</p>
              )}
            </span>
          )
          return
        }

        // Generic error without link
        if (errorMessage) {
          setErrorMessage(
            <span data-testid="general-error-message">{errorMessage}</span>
          )
          return
        }

        // Successful orders without link
        if (packageOrderId) {
          toast(
            `Order requested successfuly. Number: ${
              providerOrderId || packageOrderId
            }.`,
            'success'
          )
          closeModal()
        }
      },
      onError: (error) => {
        console.log('error', error)
        if (isEmployerConsentBlockedError(error.message)) {
          setConsentBlockMessage(error.message)
          return
        }
        // Check if error message contains "duplicate" and set flag locally
        const isDuplicateDetected =
          error.message && error.message.toLowerCase().includes('duplicate')
        setIsDuplicated(isDuplicateDetected)

        // Offer CTA to force fallback only for specific HireRight error types
        const selectedPackage = packagesList.find(
          (p) => p.value === form.values.packageId
        )
        const isHireRight =
          selectedPackage?.packageType === PackageType.HIRE_RIGHT

        if (isHireRight && !isDuplicateDetected) {
          const errorMessage = error.message || ''
          const isInvalidMissingDataError =
            errorMessage.toLowerCase().includes('invalid fields') ||
            errorMessage.toLowerCase().includes('invalid/missing data') ||
            errorMessage
              .toLowerCase()
              .includes('unable to determine ordering account') ||
            errorMessage.toLowerCase().includes('missing required field')
          // Only show correction message for specific error types
          if (isInvalidMissingDataError) {
            const providerName =
              PackageTypeLabels[selectedPackage?.packageType] || 'provider'
            setErrorMessage(
              <span data-testid="faceless-error-message">
                Please correct any missing or invalid data in the fields listed
                below and resubmit the order. Alternatively,
                <button
                  className="cursor-pointer text-blue-600 underline hover:text-blue-800"
                  onClick={() => handleForceFallback(form.values)}
                  type="button"
                >
                  click here
                </button>{' '}
                to complete the order submission within {providerName}.&nbsp;
                <p style={{ whiteSpace: 'pre-line' }}>{error.message}</p>
              </span>
            )
            return
          }
        }

        setErrorMessage(error.message)
      },
    })

  const renderSelectOption = ({ option }: { option: OptionType }) => (
    <Stack flex="1" gap="2" className="font-medium text-doubleNickel-gray-700">
      {option.label}
      <Group flex="1" gap="5">
        {option.stepTypes.map((s) => (
          <StyledBadge key={s}>{formatStepType(s, true)}</StyledBadge>
        ))}
      </Group>
    </Stack>
  )

  return (
    <Modal
      opened={isModalOpen}
      onClose={closeModal}
      classNames={{ title: 'text-xl font-bold' }}
      title="Request an order"
      centered
      size="42%"
    >
      <form
        className="grid grid-cols-1 gap-4"
        onSubmit={form.onSubmit(handleSubmit)}
      >
        <Select
          {...form.getInputProps('packageId')}
          onChange={(value) => {
            form.setFieldValue('packageId', value ?? '')
            // An override acknowledged for one package must not carry over
            // to a different package that was never flagged.
            resetAlerts()
            setIsDuplicated(false)
            setOverrideDuplicate(false)
          }}
          label="Select the provider and the package you need."
          placeholder="Select"
          withAsterisk={false}
          required
          searchable
          data={packagesList}
          disabled={!packagesList.length || createPackageOrderLoading}
          error={!!errorMessage}
          renderOption={renderSelectOption}
          classNames={{
            label: 'text-sm font-medium text-doubleNickel-gray-700',
            input: 'text-base text-doubleNickel-gray-500',
          }}
        />

        {showUdfFields &&
          udfFields.map((field) => (
            <Select
              key={field.driverIQUdfFieldId}
              data-testid={`udf-select-${field.slot}`}
              label={field.label}
              placeholder="Select"
              required={field.isRequired}
              withAsterisk={field.isRequired}
              searchable
              clearable={!field.isRequired}
              data={field.options.map((o) => ({
                value: o.driverIQUdfFieldOptionId,
                label: o.label,
              }))}
              value={form.values.driverIQUdfSelections[field.slot] ?? null}
              onChange={(v) =>
                form.setFieldValue(`driverIQUdfSelections.${field.slot}`, v)
              }
              disabled={createPackageOrderLoading}
              classNames={{
                label: 'text-sm font-medium text-doubleNickel-gray-700',
                input: 'text-base text-doubleNickel-gray-500',
              }}
            />
          ))}

        {(warningVisible || !packagesList.length) && (
          <StyledAlert
            variant="warning"
            description={
              <span data-testid="warning-message">
                Currently, this functionality is not enabled.
                <br />
                Please contact the Double Nickel team to enable it and
                accelerate your hiring process!
              </span>
            }
          />
        )}

        {errorMessage && (
          <StyledAlert variant="error" description={errorMessage} />
        )}

        {successMessage && (
          <StyledAlert variant="brand" description={successMessage} />
        )}

        {isSelectedHireRight && (
          <Checkbox
            {...form.getInputProps('fcraLegalRequirementsConsent', {
              type: 'checkbox',
            })}
            data-testid="fcra-legal-requirements"
            label={
              <span>
                I have read and complied with the{' '}
                <a
                  href="https://ows01.hireright.com/legal_requirements.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline hover:text-blue-800"
                >
                  Legal Requirements
                </a>{' '}
                for requesting a background check *
              </span>
            }
            required
          />
        )}

        {showCurrentLicenseOnly && (
          <Checkbox
            {...form.getInputProps('currentLicenseOnly', {
              type: 'checkbox',
            })}
            data-testid="current-license-only"
            label="Run checks on the current license only"
            description="Prior licenses on the application will not be sent to the provider."
          />
        )}

        {isDuplicated && (isSelectedHireRight || isDriverIQ) && (
          <Checkbox
            data-testid="duplicate-override"
            label="Ignore detected duplicate orders and continue with order
              initiation"
            // DriverIQ confirmed every repeat order is billed; HireRight has not
            description={`Ordering a duplicate ${
              isDriverIQ ? 'will' : 'may'
            } incur additional cost.`}
            checked={overrideDuplicate}
            onChange={(e) => setOverrideDuplicate(e.currentTarget.checked)}
          />
        )}

        <Divider my="md" />
        <div className="grid grid-cols-2 gap-3">
          <Button
            text="Cancel"
            className="text-base"
            variant="outline"
            onClick={closeModal}
          />
          <Button
            text="Confirm"
            className="text-base"
            type="submit"
            disabled={
              !form.values.packageId ||
              (isSelectedHireRight &&
                !form.values.fcraLegalRequirementsConsent) ||
              missingRequiredUdf
            }
            loading={createPackageOrderLoading}
          />
        </div>
      </form>

      <Modal
        opened={!!consentBlockMessage}
        onClose={closeConsentModal}
        withCloseButton={false}
        centered
        zIndex={1001}
      >
        <Stack gap="md" data-testid="employer-consent-modal">
          <h3 className="text-lg font-bold text-doubleNickel-gray-900">
            Applicant consent required
          </h3>
          <p className="text-base text-doubleNickel-gray-700">
            {consentBlockMessage}
          </p>
          <p className="text-sm text-doubleNickel-gray-500">
            Ask the applicant to update &quot;May we contact this
            employer?&quot; on their application, or update the employer entry
            on their profile once you have their permission, then request the
            order again.
          </p>
          <Button
            text="Close"
            className="text-base"
            data-testid="employer-consent-modal-close"
            onClick={closeConsentModal}
          />
        </Stack>
      </Modal>
    </Modal>
  )
}

export default RequestOrderModal
